/**
 * Inquiry service — the ONE place that decides how the contact form is sent.
 *
 * Two modes, chosen in frontend/.env (needs a rebuild/restart after changing):
 *
 *   REACT_APP_INQUIRY_MODE=mailto  (default)
 *       Opens the visitor's own email app with the message filled in.
 *       IMPORTANT: this does NOT send anything. The visitor still has to press
 *       Send in their email app, and the website cannot know whether they did.
 *       Works with no backend.
 *
 *   REACT_APP_INQUIRY_MODE=api
 *       POSTs the message to  ${REACT_APP_BACKEND_URL}/api/inquiries
 *       (backend/server.py). Success means the backend stored it (HTTP 201).
 *       If REACT_APP_BACKEND_URL is empty, the site falls back to mailto.
 *
 * The contact page (src/site/pages/ContactPage.jsx) calls validateInquiry()
 * and submitInquiry() and shows a message for each result code below.
 * Never put the admin token (ADMIN_TOKEN) in frontend code or REACT_APP_*
 * variables — everything in the frontend is public.
 */
export const CONTACT_EMAIL = "Contact@ksmartsystems.com";

export const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const INQUIRY_MODE = process.env.REACT_APP_INQUIRY_MODE === "api" && BACKEND_URL ? "api" : "mailto";
export const TIMEOUT_MS = 15000;

/** @typedef {{ name: string, company: string, email: string, topic: string, message: string, lang: "en"|"ko", website?: string }} Inquiry */

const LIMITS = { name: 120, company: 160, topic: 120, message: 5000 }; // same as backend/server.py

/** Returns { ok, errors: { field: "required" | "email" | "long" } } */
export const validateInquiry = (data) => {
  const errors = {};
  ["name", "company", "email", "message"].forEach((k) => { if (!String(data[k] ?? "").trim()) errors[k] = "required"; });
  if (!errors.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(data.email).trim())) errors.email = "email";
  Object.entries(LIMITS).forEach(([k, max]) => { if (!errors[k] && String(data[k] ?? "").length > max) errors[k] = "long"; });
  return { ok: Object.keys(errors).length === 0, errors };
};

export const buildMailto = (data) => {
  const subject = `KSS inquiry: ${data.company} / ${data.topic}`;
  const body = `Name: ${data.name}\nCompany: ${data.company}\nEmail: ${data.email}\nTopic: ${data.topic}\n\n${data.message}`;
  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

/** Error with a `code` the page can translate: timeout | network | rate | invalid | server */
export class InquiryError extends Error {
  constructor(code, detail) { super(detail || code); this.code = code; }
}

const mailtoTransport = async (data) => {
  window.location.href = buildMailto(data);
  return { status: "mail_app_opened" };
};

const apiTransport = async (data) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  let res;
  try {
    res = await fetch(`${BACKEND_URL}/api/inquiries`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: data.name.trim(), company: data.company.trim(), email: data.email.trim(),
        topic: data.topic, message: data.message.trim(), lang: data.lang,
        website: data.website || "", // spam trap, should be empty
      }),
      signal: controller.signal,
    });
  } catch (e) {
    throw new InquiryError(e?.name === "AbortError" ? "timeout" : "network", String(e?.message || e));
  } finally {
    clearTimeout(timer);
  }
  if (res.status === 429) throw new InquiryError("rate");
  if (res.status === 422 || res.status === 400) throw new InquiryError("invalid");
  if (!res.ok) throw new InquiryError("server", `HTTP ${res.status}`);
  const json = await res.json().catch(() => ({}));
  return { status: "stored", ...json }; // { id, created_at }
};

/** @param {Inquiry} data */
export const submitInquiry = (data) => (INQUIRY_MODE === "api" ? apiTransport(data) : mailtoTransport(data));
